/**
 * Schedule Page
 *
 * Combined view with tabs for:
 * - Weekly View: Schedule operators/packers/stations for the week (formerly WorkSchedule)
 * - Daily Queue: View production sessions queue for a specific day (formerly DailyWorkQueue)
 */
import React, { useEffect } from 'react';
import { CalendarDays, ListTodo } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { usePageTitle, usePageTitleContext } from '../../contexts/PageTitleContext';
import WorkScheduleContent from './WorkScheduleContent';
import DailyWorkQueueContent from './DailyWorkQueueContent';

type TabType = 'weekly' | 'daily';

const Schedule: React.FC = () => {
  const { t } = useLanguage();
  usePageTitle(t('workSchedule.title'));
  const { setHeaderTabs } = usePageTitleContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabType) || 'daily';

  const setActiveTab = (tab: TabType) => {
    setSearchParams({ tab }, { replace: true });
  };

  // Show only the active tab name in the mobile header; tap to switch
  useEffect(() => {
    const label = activeTab === 'daily' ? t('schedule.dailyQueue') : t('schedule.weeklyView');
    const nextTab: TabType = activeTab === 'daily' ? 'weekly' : 'daily';
    setHeaderTabs(
      <button
        onClick={() => setSearchParams({ tab: nextTab }, { replace: true })}
        className="text-lg font-semibold text-gray-900 dark:text-gray-100"
      >
        {label}
      </button>
    );
    return () => setHeaderTabs(null);
  }, [activeTab, t, setHeaderTabs, setSearchParams]);

  return (
    <div className="space-y-4">
      {/* Tab selector — desktop only (mobile uses header tabs) */}
      <div className="hidden sm:flex gap-6 border-b border-gray-200 dark:border-gray-700 px-4">
        <button
          onClick={() => setActiveTab('daily')}
          className={`flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'daily'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <ListTodo className="w-4 h-4" />
          {t('schedule.dailyQueue')}
        </button>
        <button
          onClick={() => setActiveTab('weekly')}
          className={`flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'weekly'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <CalendarDays className="w-4 h-4" />
          {t('schedule.weeklyView')}
        </button>
      </div>

      {/* Tab content */}
      {activeTab === 'weekly' ? (
        <WorkScheduleContent />
      ) : (
        <DailyWorkQueueContent />
      )}
    </div>
  );
};

export default Schedule;
